import { useState } from 'react';
import { Filter, Search, Loader, MapPin, Building, GraduationCap } from 'lucide-react';
import { applyAdvancedFilters } from '../../services/recommendations';

export default function FiltresAvancesPanel() {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [filters, setFilters] = useState({
    entityType: 'formations',
    geography: '',
    sector: '',
    accessibility: 'all',
    level: ''
  });
  const [error, setError] = useState(null);

  const handleApplyFilters = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await applyAdvancedFilters(
        filters.entityType,
        filters.geography || null,
        filters.sector || null,
        filters.accessibility,
        filters.level || null
      );
      setResults(result.results || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de l\'application des filtres');
      console.error('Erreur filtres:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
          <Filter className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Filtres Avancés</h2>
          <p className="text-sm text-gray-600">Recherche multicritères personnalisée</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="space-y-4 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters.entityType}
              onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="formations">Formations</option>
              <option value="certifications">Certifications RNCP</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Accessibilité</label>
            <select
              value={filters.accessibility}
              onChange={(e) => setFilters({ ...filters, accessibility: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">Toutes</option>
              <option value="online">En ligne uniquement</option>
              <option value="presence">En présentiel</option>
              <option value="hybrid">Hybride</option>
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Localisation
            </label>
            <input
              type="text"
              value={filters.geography}
              onChange={(e) => setFilters({ ...filters, geography: e.target.value })}
              placeholder="Ex: Paris, Île-de-France"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4 inline mr-1" />
              Secteur
            </label>
            <input
              type="text"
              value={filters.sector}
              onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
              placeholder="Ex: Informatique"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <GraduationCap className="w-4 h-4 inline mr-1" />
            Niveau
          </label>
          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Tous les niveaux</option>
            <option value="niveau_3">Niveau 3 (CAP)</option>
            <option value="niveau_4">Niveau 4 (BAC)</option>
            <option value="niveau_5">Niveau 5 (BAC+2)</option>
            <option value="niveau_6">Niveau 6 (BAC+3/4)</option>
            <option value="niveau_7">Niveau 7 (BAC+5)</option>
            <option value="niveau_8">Niveau 8 (Doctorat)</option>
          </select>
        </div>
      </div>

      {/* Bouton */}
      <button
        onClick={handleApplyFilters}
        disabled={loading}
        className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
      >
        {loading ? (
          <>
            <Loader className="w-5 h-5 animate-spin" />
            Recherche...
          </>
        ) : (
          <>
            <Search className="w-5 h-5" />
            Appliquer les filtres
          </>
        )}
      </button>

      {/* Erreur */}
      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Résultats */}
      {results.length > 0 && (
        <div className="mt-6">
          <h3 className="font-semibold text-gray-900 mb-4">
            {results.length} résultat{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
          </h3>

          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {results.map((item, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:border-orange-300 hover:shadow-sm transition-all"
              >
                <h4 className="font-medium text-gray-900 mb-2">
                  {item.title || item.intitule || 'Sans titre'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {item.niveau && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {item.niveau}
                    </span>
                  )}
                  {item.secteur && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {item.secteur}
                    </span>
                  )}
                  {item.modalite && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                      {item.modalite}
                    </span>
                  )}
                  {item.localisation && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {item.localisation}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && results.length === 0 && !error && (
        <div className="mt-6 text-center py-12 text-gray-500">
          <Filter className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p>Configurez vos filtres et lancez la recherche</p>
        </div>
      )}
    </div>
  );
}